import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '@/lib/utils';

import {
  authFormShellClassName,
  authSubtitleClassName,
  authTitleClassName,
} from '@/components/auth/authStyles';

type PageHeaderProps = {
  title: string;
  description?: string;
  actions?: ReactNode;
  className?: string;
};

export function PageHeader({ title, description, actions, className }: PageHeaderProps) {
  return (
    <div className={cn('flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between', className)}>
      <div className="space-y-1">
        <h1 className="text-display-2 text-brand-secondary">{title}</h1>
        {description ? (
          <p className="text-body text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

type AuthFormShellProps = {
  title?: string;
  titleDisplay?: string;
  titleSans?: string;
  description?: string;
  children: ReactNode;
  className?: string;
} & ComponentPropsWithoutRef<'div'>;

export function AuthFormShell({
  title,
  titleDisplay,
  titleSans,
  description,
  children,
  className,
  ...props
}: AuthFormShellProps) {
  const hasSplitTitle = titleDisplay !== undefined || titleSans !== undefined;
  const showHeader = hasSplitTitle || title || description;

  return (
    <div className={cn(authFormShellClassName, className)} {...props}>
      {showHeader ? (
        <header className="mb-10 space-y-2 text-center">
          {hasSplitTitle || title ? (
            <h1 className={authTitleClassName}>
              {titleDisplay ? <span className="font-display">{titleDisplay} </span> : null}
              {titleSans ? <span className="font-sans">{titleSans}</span> : null}
              {!hasSplitTitle && title ? <span className="font-display">{title}</span> : null}
            </h1>
          ) : null}
          {description ? <p className={authSubtitleClassName}>{description}</p> : null}
        </header>
      ) : null}
      {children}
    </div>
  );
}
